import { useMutation, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  Mail,
  Paperclip,
  Plus,
  RefreshCw,
  Reply,
} from "lucide-react";
import { useDataProvider, useGetList, useGetOne, useNotify } from "ra-core";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { CrmDataProvider } from "../providers/types";
import type {
  Company,
  Contact,
  EmailDraft,
  EmailMessage,
  EmailThread,
  SendMailInput,
} from "../types";
import { MAIL_ACCOUNT_QUERY_KEY, useMailAccount } from "./MailAccountSettings";

import { MailComposer, type RecipientOption } from "./MailComposer";
import {
  DEFAULT_MAIL_TEMPLATES,
  renderMailTemplate,
  type MailTemplate,
} from "../../../../supabase/functions/_shared/mailTemplates";
import { getTemplateContext } from "./templateFields";

type DraftListItem = Pick<EmailDraft, "id" | "source" | "status"> & {
  subject?: string;
  to?: string;
  message?: Pick<SendMailInput, "subject" | "to">;
};

const emptyComposer = {
  to: "",
  subject: "",
  text: "",
  purpose: "business" as const,
  attachments: [],
};

export const CompanyEmail = ({ company }: { company: Company }) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data: account, isPending: isAccountPending } = useMailAccount();
  const [composerOpen, setComposerOpen] = useState(false);
  const [composer, setComposer] = useState<SendMailInput>({
    ...emptyComposer,
    companyId: Number(company.id),
  });
  const [selectedDraft, setSelectedDraft] = useState<EmailDraft>();
  const [initialTemplate, setInitialTemplate] = useState<MailTemplate>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTemplate = searchParams.get("template");
  const builtinTemplate = DEFAULT_MAIL_TEMPLATES.find(
    (item) => item.id === requestedTemplate,
  );
  const customTemplateRequested =
    !!requestedTemplate && !builtinTemplate && /^\d+$/.test(requestedTemplate);
  const {
    data: personalTemplate,
    error: requestedTemplateError,
    refetch: retryTemplate,
  } = useGetOne<MailTemplate>(
    "email_templates",
    {
      id: customTemplateRequested ? Number(requestedTemplate) : 0,
      meta: { templateFiles: true },
    },
    { enabled: customTemplateRequested },
  );
  const openedTemplate = useRef<string | undefined>(undefined);
  const openedDraftId = useRef<string | null>(null);
  const {
    data: drafts = [],
    refetch: refetchDrafts,
    error: draftsError,
  } = useGetList<DraftListItem>(
    "email_drafts",
    {
      filter: { company_id: company.id, "status@neq": "sent" },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
      meta: {
        columns: "id,source,status,subject:message->>subject,to:message->>to",
      },
    },
    { refetchInterval: 15_000 },
  );
  const { mutate: openDraft, isPending: isDraftOpening } = useMutation({
    mutationFn: (id: string) =>
      dataProvider.getOne<EmailDraft>("email_drafts", { id }),
    onSuccess: ({ data }) => {
      setInitialTemplate(undefined);
      setSelectedDraft(data);
      setComposer(data.message);
      setComposerOpen(true);
    },
    onError: (error: Error) => notify(error.message, { type: "error" }),
  });
  useEffect(() => {
    const id = searchParams.get("draft");
    const draft = drafts.find((item) => item.id === id);
    if (draft && openedDraftId.current !== id) {
      openedDraftId.current = id;
      openDraft(draft.id);
    }
  }, [drafts, searchParams, openDraft]);
  const closeComposer = () => {
    setComposerOpen(false);
    if (searchParams.has("draft") || searchParams.has("template")) {
      const next = new URLSearchParams(searchParams);
      next.delete("draft");
      next.delete("template");
      setSearchParams(next, { replace: true });
      openedTemplate.current = undefined;
    }
  };

  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null);
  const [direction, setDirection] = useState<"all" | "incoming" | "outgoing">(
    "all",
  );
  const autoSyncedAccountId = useRef<number | null>(null);

  const {
    data: threads = [],
    refetch: refetchThreads,
    isPending: areThreadsPending,
  } = useGetList<EmailThread>("email_threads", {
    filter: { company_id: company.id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "last_message_at", order: "DESC" },
  });
  const { data: messages = [], refetch: refetchMessages } =
    useGetList<EmailMessage>("email_messages", {
      filter: { company_id: company.id },
      pagination: { page: 1, perPage: 250 },
      sort: { field: "message_at", order: "DESC" },
    });
  const { data: contacts = [], isPending: areContactsPending } =
    useGetList<Contact>("contacts", {
      filter: { company_id: company.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "last_name", order: "ASC" },
    });

  const recipients = useMemo(
    () => getRecipients(company, contacts),
    [company, contacts],
  );
  useEffect(() => {
    const template =
      builtinTemplate ||
      (customTemplateRequested ? personalTemplate : undefined);
    const key = `${company.id}:${requestedTemplate}`;
    if (
      !requestedTemplate ||
      !template ||
      isAccountPending ||
      areContactsPending ||
      searchParams.has("draft") ||
      openedTemplate.current === key
    )
      return;
    openedTemplate.current = key;
    const recipient = recipients[0];
    const rendered = renderMailTemplate(
      template,
      getTemplateContext({
        companyName: company.name,
        recipientName: recipient?.name,
        senderName: account?.display_name || account?.email,
      }),
    );
    setSelectedDraft(undefined);
    setInitialTemplate(template);
    setComposer({
      ...emptyComposer,
      ...rendered,
      attachments: template.attachments?.map((file) => ({ ...file })) || [],
      companyId: Number(company.id),
      to: recipient?.email || "",
      contactId: recipient?.contactId,
    });
    setComposerOpen(true);
  }, [
    requestedTemplate,
    builtinTemplate,
    customTemplateRequested,
    personalTemplate,
    company.id,
    company.name,
    isAccountPending,
    areContactsPending,
    searchParams,
    recipients,
    account,
  ]);
  const messagesByThread = useMemo(() => {
    const map = new Map<number, EmailMessage[]>();
    for (const message of messages) {
      const key = Number(message.thread_id);
      const list = map.get(key) || [];
      list.push(message);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort(
        (first, second) =>
          new Date(first.message_at).getTime() -
          new Date(second.message_at).getTime(),
      );
    }
    return map;
  }, [messages]);
  const filteredThreads = threads.filter(
    (thread) => direction === "all" || thread.last_direction === direction,
  );
  const selectedThread = threads.find(
    (thread) => Number(thread.id) === selectedThreadId,
  );
  const selectedMessages = selectedThreadId
    ? messagesByThread.get(selectedThreadId) || []
    : [];

  const sync = useMutation({
    mutationFn: () => dataProvider.mailSync(),
    onSuccess: async ({ imported }) => {
      await Promise.all([refetchThreads(), refetchMessages()]);
      queryClient.invalidateQueries({ queryKey: MAIL_ACCOUNT_QUERY_KEY });
      if (imported) {
        notify(`Получено новых писем: ${imported}`);
      }
    },
    onError: (error: Error) =>
      notify(error.message, { type: "error", autoHideDuration: 8000 }),
  });

  const { mutate: synchronize, isPending: isSyncPending } = sync;

  useEffect(() => {
    if (!account || autoSyncedAccountId.current === Number(account.id)) return;
    autoSyncedAccountId.current = Number(account.id);
    synchronize();
  }, [account, synchronize]);

  useEffect(() => {
    if (!account) return;
    const interval = window.setInterval(() => {
      if (!isSyncPending) synchronize();
    }, 120_000);
    return () => window.clearInterval(interval);
  }, [account, isSyncPending, synchronize]);

  const openNewMessage = () => {
    setInitialTemplate(undefined);
    const first = recipients[0];
    setSelectedDraft(undefined);
    setComposer({
      ...emptyComposer,
      companyId: Number(company.id),
      to: first?.email || "",
      contactId: first?.contactId,
    });
    setComposerOpen(true);
  };

  const openReply = (thread: EmailThread, threadMessages: EmailMessage[]) => {
    setInitialTemplate(undefined);
    setSelectedDraft(undefined);
    const latest = threadMessages.at(-1);
    const incomingSender =
      latest?.direction === "incoming" ? latest.from_jsonb[0]?.email : null;
    const outgoingRecipient = latest?.to_jsonb[0]?.email;
    const to =
      incomingSender || outgoingRecipient || recipients[0]?.email || "";
    const recipient = recipients.find((option) => option.email === to);
    setComposer({
      ...emptyComposer,
      companyId: Number(company.id),
      threadId: Number(thread.id),
      contactId: recipient?.contactId || Number(thread.contact_id) || null,
      to,
      subject: /^re:/i.test(thread.subject)
        ? thread.subject
        : `Re: ${thread.subject}`,
    });
    setSelectedThreadId(null);
    setComposerOpen(true);
  };

  if (isAccountPending) return null;

  return (
    <div className="space-y-4 py-2">
      {requestedTemplate &&
        (requestedTemplateError ||
          (!builtinTemplate && !customTemplateRequested)) && (
          <Alert variant="destructive">
            <AlertTitle>Не удалось открыть шаблон</AlertTitle>
            <AlertDescription>
              Шаблон недоступен или был изменён.{" "}
              <Link to="/email_templates" className="underline">
                Вернуться к шаблонам
              </Link>
              {customTemplateRequested && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => retryTemplate()}
                >
                  Повторить
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">
            {account?.email || "Почта не подключена"}
          </div>
          <div className="text-xs text-muted-foreground">
            {account?.last_synced_at
              ? `Синхронизировано ${new Date(account?.last_synced_at).toLocaleString()}`
              : "Синхронизация ещё не выполнялась"}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSyncPending}
            onClick={() => {
              if (account) synchronize();
              refetchDrafts();
            }}
          >
            <RefreshCw
              className={isSyncPending ? "size-4 animate-spin" : "size-4"}
            />
            Обновить
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={openNewMessage}
            disabled={!recipients.length}
          >
            <Plus className="size-4" />
            Написать
          </Button>
        </div>
      </div>

      {!account && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border p-4 text-sm">
          <Mail className="size-5" />
          <p className="flex-1">
            Черновики можно подготовить сейчас. Для отправки подключите почту.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings">Настройки почты</Link>
          </Button>
        </div>
      )}
      {draftsError && (
        <Alert variant="destructive">
          <AlertTitle>Не удалось загрузить черновики</AlertTitle>
          <AlertDescription>
            Обновите список. Если ошибка повторяется, обратитесь к
            администратору.
          </AlertDescription>
        </Alert>
      )}
      {drafts.some((draft) => draft.status !== "sent") && (
        <section aria-label="Черновики писем" className="space-y-2">
          <h3 className="text-sm font-medium">Черновики и утверждение</h3>
          <div className="divide-y rounded-md border">
            {drafts
              .filter((draft) => draft.status !== "sent")
              .map((draft) => (
                <button
                  key={draft.id}
                  type="button"
                  className="flex w-full flex-wrap items-center justify-between gap-2 p-3 text-left hover:bg-muted/40"
                  onClick={() => openDraft(draft.id)}
                  disabled={isDraftOpening}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm font-medium">
                      {draft.subject ?? draft.message?.subject}
                    </span>
                    <span className="block break-all text-xs text-muted-foreground">
                      {draft.to ?? draft.message?.to} ·{" "}
                      {draft.source === "mcp"
                        ? "Подготовлено через MCP"
                        : "Черновик"}
                    </span>
                  </span>
                  <Badge
                    variant={
                      draft.status === "failed" ? "destructive" : "outline"
                    }
                  >
                    {draft.status === "pending"
                      ? "На утверждение"
                      : draft.status === "sending"
                        ? "Отправляется"
                        : "Проверить отправку"}
                  </Badge>
                </button>
              ))}
          </div>
        </section>
      )}

      {account?.last_error && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Ошибка подключения</AlertTitle>
          <AlertDescription>{account?.last_error}</AlertDescription>
        </Alert>
      )}

      {!recipients.length && (
        <Alert>
          <AlertCircle />
          <AlertTitle>Нет адреса получателя</AlertTitle>
          <AlertDescription>
            Добавьте email контакту или заполните поле «Email из исследования» в
            карточке компании.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        {(["all", "incoming", "outgoing"] as const).map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={direction === value ? "secondary" : "ghost"}
            onClick={() => setDirection(value)}
          >
            {value === "all"
              ? "Все"
              : value === "incoming"
                ? "Входящие"
                : "Исходящие"}
          </Button>
        ))}
      </div>

      {areThreadsPending ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Загрузка писем…
        </div>
      ) : filteredThreads.length ? (
        <div className="divide-y rounded-md border">
          {filteredThreads.map((thread) => {
            const threadMessages =
              messagesByThread.get(Number(thread.id)) || [];
            const latest = threadMessages.at(-1);
            return (
              <button
                key={thread.id}
                type="button"
                className="flex w-full gap-3 p-3 text-left transition-colors hover:bg-muted/40"
                onClick={() => setSelectedThreadId(Number(thread.id))}
              >
                <div className="mt-0.5 rounded-full bg-muted p-2">
                  {thread.last_direction === "incoming" ? (
                    <ArrowDownLeft className="size-4" />
                  ) : (
                    <ArrowUpRight className="size-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <span className="truncate text-sm font-medium">
                      {thread.subject}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatDate(thread.last_message_at)}
                    </span>
                  </div>
                  <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {latest?.snippet || "Письмо без текста"}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="outline">
                      {threadMessages.length || 1}
                    </Badge>
                    {latest?.status === "failed" && (
                      <Badge variant="destructive">Ошибка отправки</Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
          Писем с этой компанией пока нет
        </div>
      )}

      {composerOpen && (
        <MailComposer
          initialTemplate={initialTemplate}
          key={selectedDraft?.id || "new"}
          company={company}
          account={account}
          initial={composer}
          draft={selectedDraft}
          recipients={recipients}
          onClose={closeComposer}
          onSaved={() => {
            refetchDrafts();
            refetchThreads();
            refetchMessages();
          }}
        />
      )}

      <Dialog
        open={selectedThreadId !== null}
        onOpenChange={(open) => !open && setSelectedThreadId(null)}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedThread?.subject || "Переписка"}</DialogTitle>
            <DialogDescription>
              {selectedMessages.length} сообщений
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {selectedMessages.map((message) => (
              <MessageCard key={message.id} message={message} />
            ))}
          </div>
          {selectedThread && (
            <DialogFooter>
              <Button
                type="button"
                onClick={() => openReply(selectedThread, selectedMessages)}
              >
                <Reply className="size-4" />
                Ответить
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MessageCard = ({ message }: { message: EmailMessage }) => {
  const addresses =
    message.direction === "incoming" ? message.from_jsonb : message.to_jsonb;
  return (
    <div
      className={`rounded-md border p-4 ${
        message.direction === "outgoing" ? "ml-6 bg-primary/5" : "mr-6"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">
          {message.direction === "incoming" ? "От" : "Кому"}:{" "}
          {formatAddresses(addresses)}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatDate(message.message_at)}
        </div>
      </div>
      {message.text_body ? (
        <div className="mt-3 whitespace-pre-wrap text-sm leading-6">
          {message.text_body}
        </div>
      ) : message.html_body ? (
        <div
          className="prose prose-sm mt-3 max-w-none dark:prose-invert"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(message.html_body, {
              FORBID_TAGS: ["img", "style"],
              FORBID_ATTR: ["style"],
            }),
          }}
        />
      ) : null}
      {!!message.attachments?.length && (
        <div className="mt-3 flex flex-wrap gap-2">
          {message.attachments.map((attachment, index) =>
            attachment.src ? (
              <Button
                key={`${attachment.title}-${index}`}
                asChild
                size="sm"
                variant="outline"
              >
                <a href={attachment.src} target="_blank" rel="noreferrer">
                  <Paperclip className="size-3" />
                  {attachment.title}
                </a>
              </Button>
            ) : (
              <Badge key={`${attachment.title}-${index}`} variant="outline">
                <Paperclip className="mr-1 size-3" /> {attachment.title}
              </Badge>
            ),
          )}
        </div>
      )}
      {message.status === "failed" && (
        <div className="mt-3 text-xs text-destructive">
          {message.error || "Ошибка отправки"}
        </div>
      )}
    </div>
  );
};

const getRecipients = (company: Company, contacts: Contact[]) => {
  const recipients = new Map<string, RecipientOption>();
  if (company.research_email) {
    const email = company.research_email.trim().toLowerCase();
    recipients.set(email, { email, label: `${email} — компания` });
  }
  for (const contact of contacts) {
    const name = [contact.first_name, contact.last_name]
      .filter(Boolean)
      .join(" ");
    for (const entry of contact.email_jsonb || []) {
      const email = entry.email?.trim().toLowerCase();
      if (!email) continue;
      recipients.set(email, {
        email,
        label: name ? `${name} — ${email}` : email,
        name: contact.first_name || name,
        contactId: Number(contact.id),
      });
    }
  }
  return Array.from(recipients.values());
};

const formatAddresses = (addresses: Array<{ email: string; name?: string }>) =>
  addresses
    .map((address) =>
      address.name ? `${address.name} <${address.email}>` : address.email,
    )
    .join(", ") || "—";

const formatDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
